/** Delivery channels for one notification, at two densities.
 *
 *  Compact: one dot per channel — filled when enabled, hollow when off. At a
 *  glance you see how many ways a message goes out and whether any are dark.
 *  Detailed: a labelled chip per channel, clickable to toggle.
 *
 *  The sparse contract lives here too: `undefined` channels render "channels
 *  not decided" (a proposal that hasn't chosen yet), `[]` renders the "no
 *  channels" warning. They must not look the same.
 */
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import type { TimelineDeliveryMethod } from './model/types';
import { methodColor } from './format';

interface Props {
  methods: TimelineDeliveryMethod[] | undefined;
  dense?: boolean;
  /** Absent ⇒ chips are read-only (no manage permission, or a proposal). */
  onToggle?: (dm: TimelineDeliveryMethod, enabled: boolean) => void;
  busyIds?: ReadonlySet<number>;
}

export function DeliveryMethodChips({ methods, dense = false, onToggle, busyIds }: Props) {
  if (methods === undefined) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
        channels not decided
      </Typography>
    );
  }
  if (methods.length === 0) {
    return (
      <Typography variant="caption" color="error.main">
        no channels
      </Typography>
    );
  }

  if (dense) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        {methods.map((dm, i) => (
          <Tooltip
            key={dm.id ?? `${dm.methodLabel}-${i}`}
            title={`${dm.methodLabel}${dm.enabled === false ? ' (off)' : ''}`}
          >
            <Box
              aria-label={`${dm.methodLabel}${dm.enabled === false ? ' off' : ''}`}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                border: `1.5px solid ${methodColor(dm.method)}`,
                bgcolor: dm.enabled === false ? 'transparent' : methodColor(dm.method),
                // Undecided reads as neither on nor off.
                opacity: dm.enabled === undefined ? 0.45 : 1,
              }}
            />
          </Tooltip>
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {methods.map((dm, i) => {
        const off = dm.enabled === false;
        const busy = dm.id !== null && busyIds?.has(dm.id);
        const clickable = Boolean(onToggle) && dm.id !== null && dm.enabled !== undefined;
        return (
          <Tooltip
            key={dm.id ?? `${dm.methodLabel}-${i}`}
            title={
              clickable
                ? `${off ? 'Enable' : 'Disable'} ${dm.methodLabel}`
                : dm.enabled === undefined
                  ? `${dm.methodLabel} — not decided yet`
                  : dm.methodLabel
            }
          >
            <Chip
              size="small"
              label={dm.methodLabel}
              variant={off ? 'outlined' : 'filled'}
              disabled={busy}
              onClick={clickable ? () => onToggle!(dm, off) : undefined}
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                cursor: clickable ? 'pointer' : 'default',
                borderColor: methodColor(dm.method),
                color: off ? methodColor(dm.method) : '#fff',
                bgcolor: off ? 'transparent' : methodColor(dm.method),
                opacity: dm.enabled === undefined ? 0.5 : 1,
                '&:hover': clickable ? { filter: 'brightness(1.1)' } : undefined,
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}
